import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Whatsapp from '@/components/atoms/Icons/Whatsapp';
import SearchBar from '@/components/atoms/SearchBar/SearchBar';
import { MODAL_CANNED_RESPONSES } from '@/components/organisms/Modals/ModalConductor';
import { Button } from '@/components/ui/button';
import { useEscapeClose } from '@/hooks/useEscapeClose';
import { useMacros } from '@/hooks/useMacros';
import { pushModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import SavedResponseRow from './SavedResponseRow';
import SavedResponsesEmptyState from './SavedResponsesEmptyState';

interface Props {
  onSelect: (text: string) => void;
  onAdd: () => void;
  onClose: () => void;
  /**
   * Opens the WhatsApp template picker. The parent computes the gating
   * (scheduled-messages flag + WhatsApp channel); undefined hides the entry.
   */
  onBrowseTemplates?: () => void;
}

const LIST_MAX_HEIGHT = 256;
// Matches the panel's `mb-2`; keeps a small gap above the conversation column.
const PANEL_GAP = 8;

const SavedResponsesPanel = ({ onSelect, onAdd, onClose, onBrowseTemplates }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { macros, deleteMacro } = useMacros();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'macros', actions.WRITE)
  );
  const [query, setQuery] = useState('');
  const [openDeleteId, setOpenDeleteId] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [listMaxHeight, setListMaxHeight] = useState(LIST_MAX_HEIGHT);

  // Clamp the list so the upward-growing panel never covers the conversation column.
  useLayoutEffect(() => {
    const panel = panelRef.current;
    const list = listRef.current;
    if (!panel || !list) {
      return;
    }
    const column = panel.closest('[data-conversation-column]');
    const recompute = () => {
      const columnTop = column?.getBoundingClientRect().top ?? 0;
      const panelRect = panel.getBoundingClientRect();
      const panelChrome = panelRect.height - list.getBoundingClientRect().height;
      const available = panelRect.bottom - columnTop - PANEL_GAP - panelChrome;
      const next = Math.min(LIST_MAX_HEIGHT, Math.max(0, available));
      setListMaxHeight((prev) => (prev === next ? prev : next));
    };
    recompute();
    if (!column) {
      return;
    }
    const observer = new ResizeObserver(recompute);
    observer.observe(column);
    return () => observer.disconnect();
  }, []);

  // Escape closes the panel, but let an open delete confirmation consume it first.
  useEscapeClose(
    useCallback(() => {
      if (!openDeleteId) {
        onClose();
      }
    }, [openDeleteId, onClose])
  );

  const filteredMacros = useMemo(() => {
    const q = query.toLowerCase().trim();
    return macros?.filter((m) => `${m.name} ${m.text}`.toLowerCase().includes(q)) ?? [];
  }, [macros, query]);

  // Without the templates entry an empty macros list keeps the empty state;
  // with it the panel must still render so templates stay reachable.
  if (!macros?.length && !onBrowseTemplates) {
    return <SavedResponsesEmptyState canWrite={canWrite} onAdd={onAdd} />;
  }

  return (
    <div
      ref={panelRef}
      className="absolute bottom-full left-0 z-10 mb-2 flex w-full flex-col gap-4 rounded-lg border border-stroke bg-background shadow-none"
    >
      <div className="flex items-center justify-between gap-3 px-3 pt-3">
        <SearchBar
          autoFocus
          className="max-w-xs flex-1 [&_input]:h-8"
          placeholder={t('chatBox.modal.search')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="flex items-center gap-3">
          {onBrowseTemplates && (
            <Button type="button" variant="ghost-neutral" size="sm" onClick={onBrowseTemplates}>
              <Whatsapp size={16} />
              {t('chatBox.templates_entry')}
            </Button>
          )}
          {canWrite && (
            <Button type="button" size="sm" onClick={onAdd}>
              <PlusIcon size={14} />
              {t('chatBox.panel.add')}
            </Button>
          )}
        </div>
      </div>

      <div ref={listRef} className="overflow-y-auto px-3 pb-3" style={{ maxHeight: listMaxHeight }}>
        {filteredMacros.map((macro) => (
          <SavedResponseRow
            key={macro.macro_id}
            macro={macro}
            isDeleteOpen={openDeleteId === macro.macro_id}
            onSelect={() => onSelect(macro.text)}
            canWrite={canWrite}
            onEdit={() => dispatch(pushModal(MODAL_CANNED_RESPONSES, { macroId: macro.macro_id }))}
            onRequestDelete={() => setOpenDeleteId(macro.macro_id)}
            onConfirmDelete={() => {
              deleteMacro(macro.macro_id);
              setOpenDeleteId(null);
            }}
            onCancelDelete={() => setOpenDeleteId(null)}
          />
        ))}

        {filteredMacros.length === 0 && (
          <div className="px-2 py-1.5 text-sm text-foreground-muted">
            {t('chatBox.no_response')}
            {query.trim() && (
              <>
                {' '}
                <button
                  type="button"
                  className="text-accent hover:underline"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() =>
                    dispatch(pushModal(MODAL_CANNED_RESPONSES, { initialName: query.trim() }))
                  }
                >
                  {t('chatBox.no_response_create', { 0: query.trim() })}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedResponsesPanel;
